/**
 * @import { CruiseSearchResult } from '@/features/world-map/types/cruise-search-result'
 */

import { getCruiseTypeLabel } from '@/features/world-map/utils/cruise';
import { codify } from '@/utils/analytics';
import { memo } from 'react';
import { getCruiseImageUrl } from './cruise-image';
import { PinIcon } from '../pin-icon';
import { TimeIcon } from '../time-icon';
import classes from './cruise.module.css';

const desktopImageVariant = { aspectRatio: '1x1-widest', width: 432, height: 432 };
const mobileImageVariant = { aspectRatio: '4x5-widest', width: 264, height: 328 };

/**
 * @typedef {React.HTMLProps<HTMLDivElement> & { cruise: CruiseSearchResult }} CruiseProps
 */

export const Cruise = memo(
  /**
   * @param {CruiseProps} props
   * @returns {React.JSX.Element}
   */
  function Cruise({ cruise, ...props }) {
    const moreLabel = `learn more about ${cruise.name}`;
    const desktopImageUrl = getCruiseImageUrl(cruise.imageUrl, desktopImageVariant);
    const mobileImageUrl = getCruiseImageUrl(cruise.imageUrl, mobileImageVariant);
    const trackingAttributes = {
      'tl-event_name': 'select_cruise',
      'tl-cruise_id': cruise.id,
      'tl-cruise_name': codify(cruise.name),
    };

    return (
      <div {...props} className={classes.cruise}>
        <a href={cruise.learnMoreUrl} target="_blank" aria-label={moreLabel} className={classes.image} {...trackingAttributes}>
          <picture>
            <source media="(min-width: 768px)" srcSet={desktopImageUrl} />
            <img src={mobileImageUrl} loading="lazy" alt="" />
          </picture>
        </a>
        <div className={classes.detail}>
          <a href={cruise.learnMoreUrl} target="_blank" aria-label={moreLabel} {...trackingAttributes}>
            <h1 className={classes.title}>{cruise.name}</h1>
          </a>
          <h2 className={classes.subtitle}>{cruise.description}</h2>
          <div className={classes.types}>
            {cruise.types.map((type) => (
              <span key={type} className={classes[type]}>
                {getCruiseTypeLabel(type)}
              </span>
            ))}
          </div>
          <div className={classes.features}>
            <div>
              <TimeIcon />
              <span>{cruise.dayCount} Days</span>
            </div>
            <div>
              <PinIcon />
              <span>{cruise.countryCount} Countries</span>
            </div>
          </div>
          <div className={classes.footer}>
            <div className={classes.priceContainer}>
              <span className={classes.priceLabel}>From</span>
              <span className={classes.priceValue}>{cruise.startingPrice}</span>
            </div>
            <span className={classes.offers}>{cruise.offers.join(' | ')}</span>
            <a
              href={cruise.learnMoreUrl}
              target="_blank"
              className={`btn ${classes.btnMore}`}
              role="button"
              aria-label={moreLabel}
              {...trackingAttributes}
            >
              Learn More
            </a>
          </div>
        </div>
      </div>
    );
  }
);
